import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowUpRight, Camera, Check, FileText, ImagePlus, Play, X } from 'lucide-react';

interface HomeComposerProps {
  prompt: string;
  onPromptChange: (value: string) => void;
  imagePreview: string | null;
  onRemoveImage: () => void;
  onOpenCamera: () => void;
  onChooseImage: () => void;
  onGenerate: () => void;
  isReady: boolean;
  errorMessage?: string;
}

export default function HomeComposer({
  prompt,
  onPromptChange,
  imagePreview,
  onRemoveImage,
  onOpenCamera,
  onChooseImage,
  onGenerate,
  isReady,
  errorMessage,
}: HomeComposerProps) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const cameraAnchorRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const restorePhotoFocus = useRef(false);
  const canGenerate = !!prompt.trim() || !!imagePreview;

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!cameraAnchorRef.current?.contains(event.target as Node)) setCameraOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCameraOpen(false);
        setExpanded(false);
        textareaRef.current?.blur();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const size = () => {
      const limit = expanded
        ? Math.max(32, Math.min(152, (window.visualViewport?.height ?? window.innerHeight) * .16))
        : 32;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, limit)}px`;
      textarea.style.overflowY = textarea.scrollHeight > limit ? 'auto' : 'hidden';
    };
    size();
    window.visualViewport?.addEventListener('resize', size);
    window.addEventListener('resize', size);
    return () => {
      window.visualViewport?.removeEventListener('resize', size);
      window.removeEventListener('resize', size);
    };
  }, [prompt, expanded]);
  useEffect(() => {
    if (imagePreview && restorePhotoFocus.current) {
      textareaRef.current?.focus({ preventScroll: true });
      restorePhotoFocus.current = false;
    }
  }, [imagePreview]);
  const choosePhoto = (action: () => void) => {
    restorePhotoFocus.current = document.activeElement === textareaRef.current;
    setCameraOpen(false);
    action();
  };

  return (
    <>
    {expanded && <button type="button" className="home-composer-backdrop" aria-label="Dismiss question composer" onClick={() => { setExpanded(false); setCameraOpen(false); textareaRef.current?.blur(); }} />}
    <div className={`home-composer-wrap ${expanded ? 'is-expanded' : ''}`}>
      {expanded && !prompt && !imagePreview && <div className="home-suggestions">
        {['How do I find the turning point of a quadratic?', 'How does Newton’s second law apply to a real problem?', 'How does DNA replication work?'].map((suggestion, index) => <button type="button" key={suggestion} onPointerDown={event => event.preventDefault()} onClick={() => { onPromptChange(suggestion); textareaRef.current?.focus({ preventScroll: true }); }}><span>{index === 0 ? <ImagePlus /> : index === 1 ? <FileText /> : <Check />}</span>{suggestion}<ArrowUpRight /></button>)}
      </div>}
      <div className="home-composer glass-panel">
        {imagePreview && (
          <div className="home-composer-image">
            <img src={imagePreview} alt="Selected homework preview" />
            <button type="button" className="home-image-remove" onPointerDown={event => event.preventDefault()} onClick={onRemoveImage} aria-label="Remove attached image">
              <X aria-hidden="true" />
            </button>
          </div>
        )}
        <div className="home-composer-entry">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(event) => onPromptChange(event.target.value)}
            placeholder="Ask LAVYN…"
            aria-label="Ask LAVYN a question"
            rows={1}
            enterKeyHint="enter"
            onFocus={() => setExpanded(true)}
            onBlur={() => { if (!prompt.trim() && !imagePreview) setExpanded(false); }}
          />
          <div className="home-camera-anchor" ref={cameraAnchorRef}>
            <button
              type="button"
              className="home-composer-camera"
              aria-label="Add a homework photo"
              aria-expanded={cameraOpen}
              aria-haspopup="menu"
              onPointerDown={(event) => event.preventDefault()}
              onClick={() => setCameraOpen((open) => !open)}
            >
              <Camera aria-hidden="true" />
            </button>
            {cameraOpen && (
              <div className="home-camera-menu" role="menu" aria-label="Add a photo">
                <button type="button" role="menuitem" onPointerDown={(event) => event.preventDefault()} onClick={() => choosePhoto(onOpenCamera)}>
                  <Camera aria-hidden="true" /> Take a photo
                </button>
                <button type="button" role="menuitem" onPointerDown={(event) => event.preventDefault()} onClick={() => choosePhoto(onChooseImage)}>
                  <ImagePlus aria-hidden="true" /> Choose an image
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="home-composer-controls">
          <button
            type="button"
            className="home-generate-button"
            aria-label="Generate Lesson"
            title="Generate Lesson"
            disabled={!canGenerate || !isReady}
            onPointerDown={(event) => event.preventDefault()}
            onClick={onGenerate}
          >
            <Play aria-hidden="true" fill="currentColor" strokeWidth={0} />
            <span>Generate Lesson</span>
          </button>
        </div>
      </div>

      {errorMessage && <p className="home-composer-error" role="alert">{errorMessage}</p>}
    </div>
    </>
  );
}
